import supabase from '../supabase/supabase';

const ATTEMPTS_TABLE = 'hms_academy_assessment_attempts_1715728000000';
const ATTEMPT_ANSWERS_TABLE = 'hms_academy_assessment_attempt_answers_1715728000000';

const RESULT_FIELDS = [
  'id',
  'status',
  'points_earned',
  'max_points',
  'score_percent',
  'passed',
  'started_at',
  'submitted_at',
  'graded_at'
].join(',');

function requireId(value, name) {
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error(`${name} is required.`);
  }

  return value;
}

function throwIfError(error, operation) {
  if (error) {
    throw new Error(
      error.message
        ? `Academy ${operation} failed: ${error.message}`
        : `Academy ${operation} failed.`
    );
  }
}

export async function startAssessmentAttempt({
  enrollmentId,
  assessmentId
}) {
  requireId(enrollmentId, 'enrollmentId');
  requireId(assessmentId, 'assessmentId');

  const { data, error } = await supabase.rpc(
    'hms_academy_assessment_start_attempt_1715728000000',
    {
      target_enrollment_id: enrollmentId,
      target_assessment_id: assessmentId
    }
  );

  throwIfError(error, 'attempt start');

  if (typeof data !== 'string' || data.trim() === '') {
    throw new Error('Academy attempt start returned no attempt ID.');
  }

  return data;
}

export async function recordAssessmentAnswer({
  attemptId,
  questionId,
  selectedOptionIds
}) {
  requireId(attemptId, 'attemptId');
  requireId(questionId, 'questionId');

  if (!Array.isArray(selectedOptionIds)) {
    throw new Error('selectedOptionIds must be an array.');
  }

  const { error } = await supabase.rpc(
    'hms_academy_assessment_record_answer_1715728000000',
    {
      target_attempt_id: attemptId,
      target_question_id: questionId,
      selected_option_ids: selectedOptionIds
    }
  );

  throwIfError(error, 'answer recording');
}

export async function submitAssessmentAttempt({ attemptId }) {
  requireId(attemptId, 'attemptId');

  const { data, error } = await supabase.rpc(
    'hms_academy_assessment_submit_attempt_1715728000000',
    {
      target_attempt_id: attemptId
    }
  );

  throwIfError(error, 'attempt submission');

  if (typeof data !== 'string' || data.trim() === '') {
    throw new Error('Academy attempt submission returned no graded attempt ID.');
  }

  return data;
}

export async function getAssessmentAttemptResult(attemptId) {
  requireId(attemptId, 'attemptId');

  const { data, error } = await supabase
    .from(ATTEMPTS_TABLE)
    .select(RESULT_FIELDS)
    .eq('id', attemptId)
    .maybeSingle();

  throwIfError(error, 'attempt result query');

  if (!data) {
    throw new Error('Academy attempt result was not found or is not accessible.');
  }

  return data;
}

export async function getOpenAssessmentAttempt({
  enrollmentId,
  assessmentId
}) {
  requireId(enrollmentId, 'enrollmentId');
  requireId(assessmentId, 'assessmentId');

  const { data, error } = await supabase
    .from(ATTEMPTS_TABLE)
    .select(
      'id,enrollment_id,assessment_id,course_version_id,attempt_number,status,started_at'
    )
    .eq('enrollment_id', enrollmentId)
    .eq('assessment_id', assessmentId)
    .eq('status', 'in_progress')
    .maybeSingle();

  throwIfError(error, 'open attempt query');

  return data ?? null;
}

export async function getAssessmentAttemptAnswers(attemptId) {
  requireId(attemptId, 'attemptId');

  const { data, error } = await supabase
    .from(ATTEMPT_ANSWERS_TABLE)
    .select('question_id,selected_option_id')
    .eq('attempt_id', attemptId);

  throwIfError(error, 'saved answers query');

  return data ?? [];
}